/**
 * MANAGEMENT (14) — metrics presentation logic. PURE (no React, no server): period-over-period delta math,
 * the alert-derivation rules, the donut/bar data shaping, and the FR number/date formatters the Overview
 * cockpit + its chart wrappers consume. Token colors are returned as `var(--nv-*)` (no raw hex, L-2).
 *
 * L-2 — the alert thresholds live in ONE frozen {@link ALERT_CONFIG} block (mirrors the server's
 * METRICS_CONFIG posture: no magic numbers scattered through the derivation). The server already carries the
 * SLA target / aged-days; these client thresholds are the WARN lines the strip fires on.
 */
import { token } from "@/design/theme";
import type { OverviewMetrics } from "./metrics.types";

/** L-2 — the alert WARN lines. Frozen; no magic number in the derivation below. */
export const ALERT_CONFIG = Object.freeze({
  /** SLA% below this fires the SLA-breach alert. */
  SLA_WARN_PCT: 90,
  /** SLA% at/below this escalates the SLA alert to danger tone. */
  SLA_DANGER_PCT: 75,
  /** churn30d at/above this fires the churn-spike alert even if it is not rising. */
  CHURN_ALERT: 3,
  /** (provisioned − synced) above this fires the onboarding-stall alert. */
  STALL_THRESHOLD: 0,
  /** unread labo replies above this fires the unread-backlog alert. */
  UNREAD_ALERT: 0,
});

// ── Number / date formatting (FR locale; figures stay LTR via .nv-ltr-island upstream) ──

const nfInt = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 });
const nfPct = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 });

/** Whole-number count, FR-grouped. */
export function fmtCount(value: number): string {
  return nfInt.format(value);
}

/** Whole-percent, FR-grouped, with the unit. */
export function fmtPct(value: number): string {
  return `${nfPct.format(value)} %`;
}

/** Short axis label for a bucket date (FR day/month for daily buckets, month/year for monthly). */
export function shortBucket(iso: string, bucket: "day" | "month"): string {
  const d = new Date(iso);
  if (bucket === "month") {
    return new Intl.DateTimeFormat("fr-FR", { month: "short", year: "2-digit" }).format(d);
  }
  return new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "2-digit" }).format(d);
}

// ── Period-over-period delta ─────────────────────────────────────────────────────────

export type DeltaDirection = "up" | "down" | "flat";

export interface Delta {
  /** current − previous. */
  diff: number;
  /** Rounded percent change vs the baseline; null when the baseline is 0 (avoid divide-by-zero / ∞). */
  pct: number | null;
  direction: DeltaDirection;
}

/** current vs previous → a signed delta. A zero baseline yields a null pct (shown as "nouveau"/dash). */
export function delta(current: number, previous: number): Delta {
  const diff = current - previous;
  const direction: DeltaDirection = diff > 0 ? "up" : diff < 0 ? "down" : "flat";
  const pct = previous === 0 ? null : Math.round((diff / previous) * 100);
  return { diff, pct, direction };
}

// ── Alert derivation ───────────────────────────────────────────────────────────────

export type AlertTone = "danger" | "warning" | "info";

/** One platform alert — the AlertStrip renders these, only the ones that fired. */
export interface AlertItem {
  id: string;
  tone: AlertTone;
  glyph: string;
  /** i18n key for the message (interpolated with `count`/`target`). */
  labelKey: string;
  count: number;
  /** Extra interpolation values for the message. */
  vars?: Record<string, string | number>;
  /** i18n key for the CTA button. */
  ctaKey: string;
  /** The route (with a pre-applied filter) the CTA navigates to. */
  to: string;
}

/**
 * Derive the live alerts from the overview metrics. Returns ONLY the alerts whose flag fired (an empty array
 * collapses the strip). Order = severity-ish: SLA → aged → churn → stall → unread. Each CTA deep-links into
 * the relevant section with a pre-applied filter.
 */
export function deriveAlerts(m: OverviewMetrics): AlertItem[] {
  const out: AlertItem[] = [];
  const { tickets, subscriptions, onboardingFunnel } = m;

  // SLA breach — fires when SLA% is known and below the WARN line. Danger when very low.
  if (tickets.slaPct != null && tickets.slaPct < ALERT_CONFIG.SLA_WARN_PCT) {
    out.push({
      id: "sla",
      tone: tickets.slaPct <= ALERT_CONFIG.SLA_DANGER_PCT ? "danger" : "warning",
      glyph: "◆",
      labelKey: "mgmt.overview.alert.sla",
      count: tickets.slaPct,
      vars: { pct: tickets.slaPct, target: tickets.slaTargetHours },
      ctaKey: "mgmt.overview.alert.sla.cta",
      to: "/management/support?statut=ouvert",
    });
  }

  // Aged open tickets — fires when any open ticket is older than the aged threshold.
  if (tickets.agedOpen > 0) {
    out.push({
      id: "aged",
      tone: "warning",
      glyph: "◷",
      labelKey: "mgmt.overview.alert.aged",
      count: tickets.agedOpen,
      vars: { count: tickets.agedOpen, days: tickets.agedDays },
      ctaKey: "mgmt.overview.alert.aged.cta",
      to: "/management/support?statut=ouvert",
    });
  }

  // Churn spike — fires when churn is rising vs the prior window OR breaches the absolute alert line.
  if (
    subscriptions.churn30d > subscriptions.churnPrev30d ||
    subscriptions.churn30d >= ALERT_CONFIG.CHURN_ALERT
  ) {
    if (subscriptions.churn30d > 0) {
      out.push({
        id: "churn",
        tone: "warning",
        glyph: "↓",
        labelKey: "mgmt.overview.alert.churn",
        count: subscriptions.churn30d,
        vars: { count: subscriptions.churn30d, prev: subscriptions.churnPrev30d },
        ctaKey: "mgmt.overview.alert.churn.cta",
        to: "/management/abonnements?statut=ANNULEE",
      });
    }
  }

  // Onboarding stall — labs provisioned but never synced an entitlement.
  const unsynced = onboardingFunnel.provisioned - onboardingFunnel.synced;
  if (unsynced > ALERT_CONFIG.STALL_THRESHOLD) {
    out.push({
      id: "stall",
      tone: "info",
      glyph: "⬡",
      labelKey: "mgmt.overview.alert.stall",
      count: unsynced,
      vars: { count: unsynced },
      ctaKey: "mgmt.overview.alert.stall.cta",
      to: "/management/abonnements",
    });
  }

  // Unread backlog — labo replies waiting to be read.
  if (tickets.unread > ALERT_CONFIG.UNREAD_ALERT) {
    out.push({
      id: "unread",
      tone: "info",
      glyph: "✉",
      labelKey: "mgmt.overview.alert.unread",
      count: tickets.unread,
      vars: { count: tickets.unread },
      ctaKey: "mgmt.overview.alert.unread.cta",
      to: "/management/support",
    });
  }

  return out;
}

// ── Chart data shaping ───────────────────────────────────────────────────────────────

/** One slice of a donut / one bar — { name, value, color }. */
export interface ChartDatum {
  key: string;
  name: string;
  value: number;
  color: string;
}

/**
 * The token color per subscription statut (a stable, daltonisme-safe ramp). ACTIVE = success, ESSAI = azure,
 * PAST_DUE/SUSPENDUE = warn/hold, EXPIREE/ANNULEE = danger/steel.
 */
export const SUB_STATUT_COLOR: Readonly<Record<string, string>> = Object.freeze({
  ESSAI: token.azure,
  ACTIVE: token.success,
  PAST_DUE: token.warn,
  SUSPENDUE: token.hold,
  EXPIREE: token.danger,
  ANNULEE: token.steel,
});

/** The token color per ticket statut. */
export const TICKET_STATUT_COLOR: Readonly<Record<string, string>> = Object.freeze({
  ouvert: token.azure,
  en_cours: token.warn,
  resolu: token.success,
  ferme: token.steel,
});

/** A categorical palette for the tickets-by-category bars (token-driven, repeats gracefully). */
export const CATEGORY_PALETTE: ReadonlyArray<string> = [
  token.azure,
  token.success,
  token.warn,
  token.hold,
  token.steel,
];

/** Shape a `Record<statut, count>` into donut data, dropping zero slices, colored by the supplied ramp + i18n. */
export function toDonutData(
  dist: Record<string, number>,
  colorBy: Readonly<Record<string, string>>,
  label: (key: string) => string,
): ChartDatum[] {
  return Object.entries(dist)
    .filter(([, v]) => v > 0)
    .map(([key, value]) => ({ key, name: label(key), value, color: colorBy[key] ?? token.steel }));
}

/** Shape a `Record<categorie, count>` into bar data, colored from the categorical palette + i18n. */
export function toBarData(
  dist: Record<string, number>,
  label: (key: string) => string,
): ChartDatum[] {
  return Object.entries(dist).map(([key, value], i) => ({
    key,
    name: label(key),
    value,
    color: CATEGORY_PALETTE[i % CATEGORY_PALETTE.length]!,
  }));
}

/** True when a distribution map has no non-zero entry (drives the donut/bar empty state). */
export function isEmptyDist(dist: Record<string, number>): boolean {
  return Object.values(dist).every((v) => v === 0);
}
